// --- src/artifact/Artifact.js
import React, { useEffect } from 'react';
import { Block } from '../components/Block';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '../AppContext';

const Artifact = ({ artifact }) => {
  const { scrollTo, setScrollTo, blockRefs, getScrollPosition, performScrollToBlock, updateScrollPosition } = useAppContext();
  const location = useLocation();
  const { state } = location;

  // Function to register block refs (unchanged)
  const setBlockRef = (ref, id, renderingContext) => {
    if (ref) {
      blockRefs.current[id] = ref;
    } else {
      delete blockRefs.current[id];
    }
  };

  // *** NEW: Effect to scroll to top when the artifact ID changes ***
  useEffect(() => {
    // This runs when the component mounts for a *new* artifact or when the artifact prop changes.
    console.log(`Artifact ${artifact.id} selected/loaded, scrolling window to top first.`);
    window.scrollTo(0, 0);
  }, [artifact.id]); // Dependency ensures this runs only when the artifact itself changes

  // Effect for handling scrolls *after* render/navigation OR restoring position
  // This will run AFTER the scroll-to-top effect above completes on initial load/change.
  useEffect(() => {
    const targetId = (state && state.scrollTo) || scrollTo;

    // Give the scroll-to-top a moment to take effect before restoring/targeting
    const restoreTimer = setTimeout(() => {
        if (targetId) {
          console.log(`Artifact useEffect[restore/target] detected targetId: ${targetId}. Calling performScrollToBlock.`);
          performScrollToBlock(targetId);
          if (state && state.scrollTo) {
            // Clear navigation state after scroll attempt
            window.history.replaceState({}, document.title);
          }
          // setScrollTo(null) is handled within performScrollToBlock
        } else if (!location.hash && (!state || !state.scrollTo)) { // Restore only if no target/hash/nav-state
          const savedPosition = getScrollPosition(artifact.id);
          if (savedPosition > 0) {
              window.scrollTo(0, savedPosition); // Use simple scrollTo for restoration
              console.log(`Restored scroll position for ${artifact.id} to ${savedPosition}`);
          } else {
               console.log(`Not restoring scroll for ${artifact.id}, saved position is 0 or less.`);
          }
        }
    }, 50); // A small delay (50ms) allows scroll-to-top to render first

    return () => clearTimeout(restoreTimer);

  }, [artifact.id, state, scrollTo, location.hash, getScrollPosition, performScrollToBlock]);


  // Effect for saving scroll position ONLY on browser unload/refresh (unchanged from previous fix)
  useEffect(() => {
    const handleBeforeUnload = () => {
        if (artifact.id) {
            updateScrollPosition(artifact.id);
            console.log(`Saved scroll position via beforeunload for ${artifact.id}`);
        }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [artifact.id, updateScrollPosition]);


  // Render artifact content (unchanged structure)
  if (!artifact) {
    return <div className="text-sm text-gray-500">Artifact not found.</div>;
  }

  const renderChildren = (children, level = 0, blockRefs, renderingContext = "default", referencingBlockId = null) => {
    if (!children || children.length === 0) return null;
    const indentClasses = ["ml-0", "ml-1", "ml-2", "ml-3", "ml-4"];
    const responsiveIndentClasses = ["sm:ml-0", "sm:ml-2", "sm:ml-4", "sm:ml-8", "sm:ml-16"];
    const indentClass = indentClasses[Math.min(level, indentClasses.length - 1)];
    const responsiveIndentClass = responsiveIndentClasses[Math.min(level, responsiveIndentClasses.length - 1)];
    return (
      <div className={`${children.length === 1 ? 'single-child' : ''} ${indentClass} ${responsiveIndentClass}`}>
        {children.map(child => (
          <Block
            key={child.id}
            block={child}
            renderChildren={(nestedChildren, levelR, blockRefs, contextR, refIdR) => renderChildren(nestedChildren, levelR + 1, blockRefs, contextR, refIdR)}
            setBlockRef={setBlockRef}
            blockRefs={blockRefs}
            renderingContext={renderingContext}
            referencingBlockId={referencingBlockId}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="border-t border-b md:border md:rounded-lg px-4 md:px-6 py-4 md:my-4 bg-gray-50 shadow-sm" id={`artifact-${artifact.id}`}>
      <div className="text-xs text-gray-500">Type: {artifact.type}</div>
      <h1 className="text-3xl font-bold mb-4">{artifact.name}</h1>
      {artifact.children ? renderChildren(artifact.children, 0, blockRefs) : <div className="text-sm text-gray-500">No content</div>}
    </div>
  );
};

export default Artifact;